// src/components/rodeoAi/Disclaimer.jsx
import React, { useState } from 'react';
import { Box, Paper, Typography, Alert, Collapse, Button } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';

const Disclaimer = () => {
    const [expanded, setExpanded] = useState(false);

    return (
        <Box
            sx={{
                mb: { xs: 1, sm: 1.5, md: 2 },
                // FIXED HEIGHT - CANNOT GROW
                maxHeight: { xs: '180px', sm: '200px', md: '220px' },
                overflowY: 'auto',
                overflowX: 'hidden'
            }}
        >
            {/* Main Disclaimer Alert */}
            <Alert
                severity="warning"
                icon={<WarningAmberIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />}
                sx={(theme) => ({
                    mb: expanded ? { xs: 1, sm: 1.5 } : 0,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255, 152, 0, 0.15)'
                            : 'rgba(255, 152, 0, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border:
                        theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 152, 0, 0.3)'
                            : '1px solid rgba(255, 152, 0, 0.2)',
                    '& .MuiAlert-message': {
                        width: '100%',
                        fontSize: { xs: '0.75rem', sm: '0.813rem', md: '0.875rem' }
                    }
                })}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                fontSize: { xs: '0.813rem', sm: '0.875rem', md: '0.9375rem' }
                            }}
                        >
                            ⚠️ Important Disclaimer
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                mb: expanded ? { xs: 0.5, sm: 1 } : 0,
                                fontSize: { xs: '0.75rem', sm: '0.813rem', md: '0.875rem' }
                            }}
                        >
                            Market Rodeo AI provides <strong>information only</strong>, not financial, investment,
                            tax, or legal advice.
                        </Typography>
                    </Box>

                    <Button
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            textTransform: 'none',
                            minHeight: 0,
                            padding: { xs: '2px 6px', sm: '4px 8px' },
                            flexShrink: 0
                        }}
                    >
                        {expanded ? 'Less' : 'More'}
                    </Button>
                </Box>

                <Collapse in={expanded}>
                    <Box
                        component="ul"
                        sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.813rem' },
                            pl: { xs: 2, sm: 2 },
                            mt: { xs: 0.75, sm: 1 },
                            mb: 0,
                            '& li': { mb: { xs: 0.25, sm: 0.5 } }
                        }}
                    >
                        <li>Market data may be delayed or inaccurate</li>
                        <li>AI responses may contain errors or outdated information</li>
                        <li>Past performance does not guarantee future results</li>
                        <li>Always verify information independently</li>
                        <li>Consult a licensed financial advisor before making investment decisions</li>
                    </Box>
                </Collapse>
            </Alert>

            {/* Additional Info Box */}
            <Collapse in={expanded}>
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        p: { xs: 1, sm: 1.5, md: 2 },
                        backgroundColor:
                            theme.palette.mode === 'dark'
                                ? 'rgba(33, 150, 243, 0.1)'
                                : 'rgba(33, 150, 243, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border:
                            theme.palette.mode === 'dark'
                                ? '1px solid rgba(33, 150, 243, 0.2)'
                                : '1px solid rgba(33, 150, 243, 0.15)',
                        borderRadius: { xs: 1.5, sm: 2 }
                    })}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 0.75, sm: 1 } }}>
                        <InfoIcon sx={{
                            color: 'info.main',
                            fontSize: { xs: 16, sm: 18, md: 20 },
                            mt: 0.25,
                            flexShrink: 0
                        }} />
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    mb: { xs: 0.5, sm: 0.5 },
                                    fontSize: { xs: '0.75rem', sm: '0.813rem', md: '0.875rem' }
                                }}
                            >
                                How to Use This Tool Safely
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mb: { xs: 0.25, sm: 0.5 },
                                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.813rem' }
                                }}
                            >
                                • Use this AI as a research assistant, not a financial advisor
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mb: { xs: 0.25, sm: 0.5 },
                                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.813rem' }
                                }}
                            >
                                • Cross-reference all data with official sources
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.813rem' }
                                }}
                            >
                                • Consider your personal financial situation, risk tolerance, and goals
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Collapse>
        </Box>
    );
};

export default Disclaimer;